import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { BACKEND_URL } from "./api";

const RealtimeContext = createContext(null);

/**
 * Opens a single authenticated WebSocket for the logged-in user and fans
 * incoming events out via a subscription registry.
 *
 * Usage:
 *   const { subscribe, connected } = useRealtime();
 *   useEffect(() => subscribe("notification", (n) => ...), [subscribe]);
 */
export function RealtimeProvider({ children }) {
    const { user } = useAuth();
    const [connected, setConnected] = useState(false);
    const wsRef = useRef(null);
    const reconnectTimeout = useRef(null);
    const subscribersRef = useRef(new Map()); // kind -> Set<fn>
    const heartbeat = useRef(null);

    const dispatch = useCallback((kind, data) => {
        const subs = subscribersRef.current.get(kind);
        if (subs) subs.forEach((fn) => {
            try { fn(data); } catch (e) { console.error("WS sub error", e); }
        });
    }, []);

    const connect = useCallback(() => {
        if (!user) return;
        // Don't hammer reconnects with no signal (e.g. at the trailhead) — wait
        // for the 'online' event instead. Saves battery in the field.
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;
        // Derive ws URL from backend URL (http→ws, https→wss).
        const httpBase = BACKEND_URL || window.location.origin;
        const wsUrl = httpBase.replace(/^http/, "ws") + "/api/ws";
        let ws;
        try {
            ws = new WebSocket(wsUrl);
        } catch (e) {
            console.warn("WS construct failed", e);
            return;
        }
        wsRef.current = ws;

        ws.onopen = () => {
            setConnected(true);
            // Keep socket alive through ingress idle timeouts.
            heartbeat.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send("ping");
            }, 25000);
        };
        ws.onmessage = (ev) => {
            try {
                const { kind, data } = JSON.parse(ev.data);
                dispatch(kind, data);
            } catch { /* ignore */ }
        };
        ws.onerror = () => { /* let onclose handle it */ };
        ws.onclose = () => {
            setConnected(false);
            if (heartbeat.current) {
                clearInterval(heartbeat.current);
                heartbeat.current = null;
            }
            wsRef.current = null;
            // Reconnect after 3s — but only if we're actually online.
            if (user && (typeof navigator === "undefined" || navigator.onLine !== false)) {
                reconnectTimeout.current = setTimeout(connect, 3000);
            }
        };
    }, [user, dispatch]);

    useEffect(() => {
        if (!user) {
            if (wsRef.current) wsRef.current.close();
            return;
        }
        connect();
        return () => {
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
            if (heartbeat.current) clearInterval(heartbeat.current);
            if (wsRef.current) {
                wsRef.current.onclose = null; // prevent reconnect
                wsRef.current.close();
                wsRef.current = null;
            }
            setConnected(false);
        };
    }, [user, connect]);

    // Reconnect promptly when connectivity returns; stop trying when it drops.
    useEffect(() => {
        if (!user) return;
        const onOnline = () => {
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
            if (!wsRef.current) connect();
        };
        window.addEventListener("online", onOnline);
        return () => window.removeEventListener("online", onOnline);
    }, [user, connect]);

    const subscribe = useCallback((kind, fn) => {
        const map = subscribersRef.current;
        if (!map.has(kind)) map.set(kind, new Set());
        map.get(kind).add(fn);
        return () => {
            const set = map.get(kind);
            if (set) {
                set.delete(fn);
                if (set.size === 0) map.delete(kind);
            }
        };
    }, []);

    return (
        <RealtimeContext.Provider value={{ connected, subscribe }}>
            {children}
        </RealtimeContext.Provider>
    );
}

export function useRealtime() {
    const ctx = useContext(RealtimeContext);
    // Fallback no-op context so components never crash when mounted outside the provider.
    return ctx || { connected: false, subscribe: () => () => {} };
}
